import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { formatUnits } from 'viem';

import { CryptexPriceFeedUrl } from "../../constants/network";
import { ChainIndex, getIndexContract, IndexMetadata, SupportedIndexes, SupportedPriceFeeds } from "../../constants/indexes";
import { PricesApiTimeIntervals } from '../../components/Charts/constants';
import { useAddress, useChainId } from '../network';

const qs = require("qs");

const OneDay = 24 * 60 * 60 * 1000;
const PricesPageSize = 2400

export const useIndexChartPrices = (index: SupportedIndexes, interval: PricesApiTimeIntervals, fromTimestamp: number, toTimestamp?: number) => {
  const chainId = useChainId();
  const feedId = ChainIndex[chainId][index]?.priceFeeds[SupportedPriceFeeds.pyth];

  return useInfiniteQuery({
    queryKey: ['indexPrices', feedId, fromTimestamp, toTimestamp, interval],
    enabled: !!feedId,
    queryFn: async ({ pageParam }) => {
      const params = qs.stringify({
        feed_id: feedId,
        timestamp_lt: pageParam.toTimestamp || Math.ceil((new Date().getTime()) / 1000),
        interval,
        count: PricesPageSize,
      });

      let prices = new Array<{ time: number, value: number }>();
      const response = await fetch(`${CryptexPriceFeedUrl}?${params}`)
      if (response.ok) { 
        prices = await response.json()
        prices = prices.map((price: any) => ({ time: price.time, value: price.value }))
      }

      let nextPageParam = undefined;
      if (prices.length > 0) { 
        nextPageParam = fromTimestamp < prices[prices.length - 1].time && prices.length === PricesPageSize
          ? { toTimestamp: prices[prices.length - 1].time }
          : undefined
      }

      return {
        prices: prices,
        nextPageParam,
      }
    },
    initialPageParam: {
      toTimestamp: toTimestamp
    },
    getNextPageParam: (lastPage) => lastPage?.nextPageParam,
  })
}


export const useIndexPriceLastIntervalUpdate = (
  index: SupportedIndexes,
  interval: PricesApiTimeIntervals,
  refetchInterval: number = 2000,
) => {
  const chainId = useChainId();
  const feedId = ChainIndex[chainId][index]?.priceFeeds[SupportedPriceFeeds.pyth];

  return useQuery({
    queryKey: ['IndexChartLastUpdate', feedId, interval],
    enabled: !!feedId,
    refetchInterval,
    queryFn: async () => {
      let lastPrice = { time: 0, value: 0 }

      const params = qs.stringify({
        feed_id: feedId,
        interval,
        count: 1,
      });
      
      const response = await fetch(`${CryptexPriceFeedUrl}?${params}`)
      if (response.ok) { 
        const data = await response.json()
        if (data.length > 0) { 
          lastPrice = { time: data[0].time, value: data[0].value }
        }
      }

      return lastPrice
    }
  })
}


// Fetch live prices for the index
export const fetchIndexLatestPrices = async (feedId: string | undefined, timestamp: number) => { 
  let prices = new Array<{ time: number, value: number }>();
  if (!feedId) return prices;

  const params = qs.stringify({
    feed_id: feedId,
    count: 2,
  });

  const response = await fetch(`${CryptexPriceFeedUrl}?${params}`)
    if (response.ok) { 
      const data = await response.json()
      if (data.length > 0) {
        prices = data.map((price: any) => ({ time: price.time, value: price.value }))
      }
    }

  return prices;
}

export const useIndexLastestsPrices = (index: SupportedIndexes) => { 
  const chainId = useChainId();
  const feedId = ChainIndex[chainId][index]?.priceFeeds[SupportedPriceFeeds.pyth];

  return useQuery({
    queryKey: ['indexLastPrice', feedId],
    enabled: !!feedId,
    refetchInterval: 2000,
    queryFn: async () => {
      return await fetchIndexLatestPrices(feedId, Math.ceil((new Date().getTime()) / 1000))
    }
  })
}

export const use24hPriceChange = (index: SupportedIndexes) => { 
  const chainId = useChainId();
  const { data: latestPrices } = useIndexLastestsPrices(index);
  const feedId = ChainIndex[chainId][index]?.priceFeeds[SupportedPriceFeeds.pyth];

  return useQuery({
    queryKey: ['24hIndexPriceChange', latestPrices],
    enabled: !!latestPrices && !!feedId,
    queryFn: async () => {
      let percentChange = { change: 0, isPostive: true };
      if (!latestPrices || latestPrices.length === 0) return percentChange;

      const timeElapsed = (new Date().getTime() - OneDay) / 1000;
      const params = qs.stringify({
        feed_id: feedId,
        timestamp_lt: Math.ceil(timeElapsed),
        count: 1,
      });

      const response = await fetch(`${CryptexPriceFeedUrl}?${params}`)
      if (response.ok) { 
        const data = await response.json()
        if (data.length > 0) {
          percentChange = { 
            change: ((latestPrices[0].value - data[0].value) / data[0].value) * 100,
            isPostive: latestPrices[0].value >= data[0].value 
          }
        }
      }

      return percentChange;
    }
  })
}


export const useSupportedIndexesBalance = () => {
  const chainId = useChainId()
  const { address: userAddress } = useAddress()

  return useQuery({
    queryKey: ['SupportedIndexesBalance', chainId, userAddress],
    enabled: !!userAddress,
    refetchInterval: 60000,
    queryFn: async () => {
      const balances = {} as Record<SupportedIndexes, { balance: number, balanceBI: bigint }>;
      
      if (!userAddress) return balances;

      const balancesCalls = Object.keys(SupportedIndexes).map((token) => { 
        const contract = getIndexContract(chainId, token as SupportedIndexes)
        if (contract) return contract.read.balanceOf([userAddress]);
        return undefined;
      })

      const response = await Promise.all(balancesCalls)
      Object.keys(SupportedIndexes).forEach((token, index) => {
        if (response[index] !== undefined) {
          const tokenMetadata = IndexMetadata[token as SupportedIndexes]
          balances[token as SupportedIndexes] = {
            balance: parseFloat(formatUnits(response[index] || 0n, tokenMetadata.decimals)),
            balanceBI: response[index] || 0n
          }
        }
      })

      return balances
    },
  })
}